/**
 * "Daten-Reset" — danger zone dialog with a 10-second countdown before the
 * confirm button activates. Allows the operator to back out at any time.
 *
 * Used by:
 *  • Salon-Admin in /cms/settings → eigener Salon
 *  • Super-Admin in /admin → pro Salon (mit salonId-Prop)
 */
import { useEffect, useState } from "react";
import { AlertTriangle, X, Loader2, CheckCircle2, Trash2 } from "lucide-react";
import api from "@/lib/api";

const COUNTDOWN_SECONDS = 10;

export default function DataResetDialog({ open, onClose, salonId, salonName, onDone }) {
  const [scope, setScope] = useState("bookings_customers");
  const [confirmText, setConfirmText] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) { setConfirmText(""); setSecondsLeft(COUNTDOWN_SECONDS); setResult(null); setErr(""); setScope("bookings_customers"); return undefined; }
    setSecondsLeft(COUNTDOWN_SECONDS);
    const t = setInterval(() => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [open, scope, confirmText]);

  if (!open) return null;

  const requiredPhrase = "LÖSCHEN";
  const phraseOk = confirmText.trim() === requiredPhrase;
  const canSubmit = secondsLeft === 0 && phraseOk && !submitting && !result;

  const submit = async () => {
    setSubmitting(true); setErr("");
    try {
      const payload = { confirm: true, scope };
      if (salonId) payload.salon_id = salonId;
      const { data } = await api.post("/cms/reset-data", payload);
      setResult(data.cleared || {});
      if (onDone) onDone(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Fehler beim Löschen");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.55)" }} onClick={onClose} data-testid="reset-dialog-backdrop">
      <div className="w-full max-w-lg rounded-2xl overflow-hidden" style={{ background: "var(--mv-bg)" }} onClick={(e) => e.stopPropagation()} data-testid="reset-dialog">
        <div className="px-6 py-5 flex items-start justify-between gap-3" style={{ background: "#FEE2E2", borderBottom: "1px solid #FCA5A5" }}>
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 flex-shrink-0 mt-0.5" strokeWidth={1.6} style={{ color: "#991B1B" }} />
            <div>
              <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: "#991B1B" }}>Danger zone</p>
              <h2 className="font-display text-2xl" style={{ color: "#7F1D1D" }}>Daten-Reset</h2>
              {salonName && <p className="text-xs mt-1" style={{ color: "#7F1D1D" }}>für <strong>{salonName}</strong></p>}
            </div>
          </div>
          <button onClick={onClose} className="p-1 -m-1 rounded hover:bg-red-200" data-testid="reset-dialog-close"><X className="w-5 h-5" strokeWidth={1.5} style={{ color: "#991B1B" }} /></button>
        </div>

        {result ? (
          <div className="p-6 text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3" strokeWidth={1.5} style={{ color: "#10B981" }} />
            <h3 className="font-display text-2xl mb-3">Reset erfolgreich</h3>
            <div className="text-sm space-y-1 mb-6" data-testid="reset-result">
              {Object.entries(result).map(([k, v]) => (
                <p key={k}>{v} <span className="opacity-70">{LABEL[k] || k}</span></p>
              ))}
            </div>
            <button onClick={onClose} className="mv-btn">Schließen</button>
          </div>
        ) : (
          <div className="p-6 space-y-5">
            <p className="text-sm">
              Diese Aktion <strong>löscht unwiderruflich</strong> die ausgewählten Daten. Es gibt keinen
              „Rückgängig"-Knopf. Stelle sicher, dass du das wirklich möchtest.
            </p>

            <div>
              <p className="mv-label mb-2">Was soll gelöscht werden?</p>
              <div className="space-y-2 text-sm">
                {[
                  { v: "bookings_customers", l: "Alle Termine UND alle Kunden (inkl. Treuepunkte)" },
                  { v: "bookings", l: "Nur alle Termine" },
                  { v: "customers", l: "Nur alle Kunden (inkl. Treuepunkte) — Termine bleiben" },
                ].map((o) => (
                  <label key={o.v} className="flex items-start gap-2 p-2.5 rounded border cursor-pointer" style={{ borderColor: scope === o.v ? "#DC2626" : "var(--mv-border)", background: scope === o.v ? "#FEE2E2" : "transparent" }} data-testid={`reset-scope-${o.v}`}>
                    <input type="radio" checked={scope === o.v} onChange={() => setScope(o.v)} className="mt-0.5" />
                    <span>{o.l}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1.5">
                Tippe <code className="px-1.5 py-0.5 rounded bg-[var(--mv-surface)] font-mono">LÖSCHEN</code>, um zu bestätigen
              </label>
              <input
                type="text"
                className="mv-input font-mono"
                placeholder="LÖSCHEN"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                data-testid="reset-confirm-input"
                autoFocus
              />
            </div>

            {err && <p className="text-sm text-red-600" data-testid="reset-error">{err}</p>}

            <div className="flex items-center justify-between gap-3 pt-2 border-t" style={{ borderColor: "var(--mv-border)" }}>
              <button type="button" onClick={onClose} className="mv-btn-ghost" data-testid="reset-cancel-btn">Abbrechen</button>
              <button
                type="button"
                onClick={submit}
                disabled={!canSubmit}
                data-testid="reset-confirm-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium transition-colors disabled:cursor-not-allowed"
                style={{
                  background: canSubmit ? "#DC2626" : "var(--mv-surface)",
                  color: canSubmit ? "#fff" : "var(--mv-text-muted)",
                  opacity: canSubmit ? 1 : 0.7,
                }}
                title={
                  secondsLeft > 0
                    ? `Aktiv in ${secondsLeft}s — bitte warten`
                    : (!phraseOk ? "Bestätigungstext fehlt" : "Klicken zum endgültigen Löschen")
                }
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" strokeWidth={1.6} />}
                {secondsLeft > 0 ? (
                  <span data-testid="reset-countdown">Aktiv in {secondsLeft}s…</span>
                ) : (
                  <span>Jetzt unwiderruflich löschen</span>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const LABEL = {
  bookings: "Termine gelöscht",
  customers: "Kunden gelöscht",
  loyalty_transactions: "Treuepunkt-Bewegungen gelöscht",
  loyalty_redemptions: "Eingelöste Belohnungen gelöscht",
};
